import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../utils/api';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [screen, setScreen] = useState('login');
  const [token, setToken] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loanTransactions, setLoanTransactions] = useState([]);
  const [loans, setLoans] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [initializing, setInitializing] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreTransactions, setHasMoreTransactions] = useState(true);
  const [initialFormData, setInitialFormData] = useState(null);

  // Load token from localStorage on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('buxfer_token');
    if (savedToken) {
      setToken(savedToken);
      autoLogin(savedToken);
    } else {
      setInitializing(false);
    }
  }, []);

  // Reset states when screen changes
  useEffect(() => {
    setError('');
    setLoading(false);
  }, [screen]);

  const autoLogin = async (savedToken) => {
    try {
      setLoading(true);
      const [accountsData, transactionsData, loansData, contactsData] = await Promise.all([
        api.fetchAccounts(savedToken),
        api.fetchTransactions(savedToken),
        api.fetchLoans(savedToken),
        api.fetchContacts(savedToken)
      ]);
      setAccounts(accountsData);
      setTransactions(transactionsData);
      setLoans(loansData);
      setContacts(contactsData);
      setScreen('dashboard');
    } catch (err) {
      console.error('Auto-login failed:', err);
      localStorage.removeItem('buxfer_token');
    } finally {
      setLoading(false);
      setInitializing(false);
    }
  };

  const login = async (tokenValue) => {
    try {
      setLoading(true);
      setError('');
      await api.login(tokenValue);
      localStorage.setItem('buxfer_token', tokenValue);
      setToken(tokenValue);
      await autoLogin(tokenValue);
    } catch (err) {
      setError('Invalid token. Please check and try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('buxfer_token');
    setToken('');
    setAccounts([]);
    setTransactions([]);
    setLoans([]);
    setContacts([]);
    setSelectedAccount(null);
    setSelectedTransaction(null);
    setSelectedLoan(null);
    setScreen('login');
  };

  const refreshData = async () => {
    try {
      setLoading(true);
      const [accountsData, transactionsData, loansData, contactsData] = await Promise.all([
        api.fetchAccounts(token),
        api.fetchTransactions(token),
        api.fetchLoans(token),
        api.fetchContacts(token)
      ]);
      setAccounts(accountsData);
      setTransactions(transactionsData);
      setLoans(loansData);
      setContacts(contactsData);
    } catch (err) {
      console.error('Error refreshing data:', err);
      setError('Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccountTransactions = async (accountId, page = 1, append = false) => {
    try {
      const txList = await api.fetchTransactions(token, accountId, page);
      if (append) {
        setTransactions(prev => [...prev, ...txList]);
      } else {
        setTransactions(txList);
      }
      setHasMoreTransactions(txList.length === 100);
      setCurrentPage(page);
      return txList;
    } catch (err) {
      console.error('Error fetching transactions:', err);
      throw err;
    }
  };

  const fetchLoanTransactionsForContact = async (contactId) => {
    try {
      setLoanTransactions([]);
      const txList = await api.fetchLoanTransactions(token, contactId);
      setLoanTransactions(txList);
      return txList;
    } catch (err) {
      console.error('Error fetching loan transactions:', err);
      throw err;
    }
  };

  const goToLoans = async () => {
    setScreen('loans');
    setLoading(true);
    try {
      const loansData = await api.fetchLoans(token);
      setLoans(loansData);
    } catch (err) {
      console.error('Error fetching loans:', err);
    } finally {
      setLoading(false);
    }
  };

  const goToLoanDetail = async (loan) => {
    setSelectedLoan(loan);
    setScreen('loanDetail');
    setLoanTransactions([]);

    let contactsList = contacts;
    if (contactsList.length === 0) {
      contactsList = await api.fetchContacts(token);
      setContacts(contactsList);
    }

    const contact = contactsList.find(c => c.name === loan.entity);
    if (contact) {
      setLoading(true);
      try {
        await fetchLoanTransactionsForContact(contact.id);
      } finally {
        setLoading(false);
      }
    }
  };

  const getAccountName = (accountId) => {
    const account = accounts.find(acc => acc.id === accountId);
    return account ? account.name : 'Unknown';
  };

  const addTransaction = async (formData) => {
    try {
      setLoading(true);
      setError('');

      let amount;
      if (formData.type === 'expense') {
        amount = -Math.abs(parseFloat(formData.amount));
      } else {
        amount = Math.abs(parseFloat(formData.amount));
      }

      const transactionData = {
        amount: amount.toString(),
        type: formData.type,
        status: formData.status
      };

      if (formData.type === 'transfer') {
        transactionData.description = formData.description || 'Transfer';
        transactionData.fromAccountId = formData.accountId;
        transactionData.toAccountId = formData.toAccountId;
        if (formData.tags && formData.tags.trim()) {
          transactionData.tags = formData.tags.trim();
        }
      } else if (formData.type === 'loan') {
        if (formData.accountId) {
          transactionData.accountId = formData.accountId;
        }
        const contact = formData.loanContact.trim();
        const loanDescription = formData.loanDirection === 'borrowed'
          ? `Prestamo por ${contact}`
          : `Presamo a ${contact}`;
        transactionData.description = `${loanDescription} ${formData.description}` ;
        transactionData.tags = `Prestamos,${contact}`;
        if (formData.loanDirection === 'borrowed') {
          transactionData.loanedBy = contact;
          transactionData.borrowedBy = 'me';
        } else {
          transactionData.loanedBy = 'me';
          transactionData.borrowedBy = contact;
        }
      } else {
        transactionData.description = formData.description || 'Transaction';
        transactionData.accountId = formData.accountId;
        if (formData.tags && formData.tags.trim()) {
          transactionData.tags = formData.tags.trim();
        }
      }

      await api.addTransaction(token, transactionData);
      return true;
    } catch (err) {
      console.error('Error adding transaction:', err);
      setError(err.message || 'Error adding transaction');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateTransaction = async (transactionId, formData) => {
    try {
      setLoading(true);
      setError('');

      let amount;
      if (formData.type === 'expense') {
        amount = -Math.abs(parseFloat(formData.amount));
      } else {
        amount = Math.abs(parseFloat(formData.amount));
      }

      const transactionData = {
        amount: amount.toString(),
        type: formData.type,
        status: formData.status
      };

      if (formData.type === 'transfer') {
        transactionData.description = formData.description || 'Transfer';
        transactionData.fromAccountId = formData.accountId;
        transactionData.toAccountId = formData.toAccountId;
        if (formData.tags && formData.tags.trim()) {
          transactionData.tags = formData.tags.trim();
        }
      } else if (formData.type === 'loan') {
        if (formData.accountId) {
          transactionData.accountId = formData.accountId;
        }
        const contact = formData.loanContact.trim();
        const loanDescription = formData.loanDirection === 'borrowed'
          ? `Prestamo por ${contact}`
          : `Prestamo a ${contact}`;
        transactionData.description = `${loanDescription} ${formData.description}`;
        transactionData.tags = `Prestamos,${contact}`;
        if (formData.loanDirection === 'borrowed') {
          transactionData.loanedBy = contact;
          transactionData.borrowedBy = 'me';
        } else {
          transactionData.loanedBy = 'me';
          transactionData.borrowedBy = contact;
        }
      } else {
        transactionData.description = formData.description || 'Transaction';
        transactionData.accountId = formData.accountId;
        if (formData.tags && formData.tags.trim()) {
          transactionData.tags = formData.tags.trim();
        }
      }

      await api.editTransaction(token, transactionId, transactionData);
      return true;
    } catch (err) {
      console.error('Error updating transaction:', err);
      setError(err.message || 'Error updating transaction');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteTransaction = async (transactionId) => {
    try {
      setLoading(true);
      setError('');
      await api.deleteTransaction(token, transactionId);
      await refreshData();
      return true;
    } catch (err) {
      console.error('Error deleting transaction:', err);
      setError(err.message || 'Error deleting transaction');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const settleLoan = async (loan, amount, accountId) => {
    try {
      setLoading(true);
      setError('');

      const balance = parseFloat(loan.balance) || 0;
      const isOwed = balance > 0;
      const contact = loan.entity;
      const settlementAmount = Math.abs(parseFloat(amount));

      const transactionData = {
        accountId,
        description: `Loan Settlement with ${contact}`,
        amount: settlementAmount.toString(),
        type: 'settlement',
        status: 'cleared'
      };

      if (isOwed) {
        transactionData.settlePaidBy = contact;
        transactionData.settlePaidFor = 'me';
        transactionData.paidBy = contact;
        transactionData.paidFor = 'me';
      } else {
        transactionData.settlePaidBy = 'me';
        transactionData.settlePaidFor = contact;
        transactionData.paidBy = 'me';
        transactionData.paidFor = contact;
      }

      await api.addTransaction(token, transactionData);
      return true;
    } catch (err) {
      console.error('Error settling loan:', err);
      setError(err.message || 'Error settling loan');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const value = {
    // State
    screen,
    token,
    accounts,
    transactions,
    loanTransactions,
    loans,
    contacts,
    selectedAccount,
    selectedTransaction,
    selectedLoan,
    loading,
    error,
    initializing,
    currentPage,
    hasMoreTransactions,

    // Setters
    setScreen,
    setToken,
    setAccounts,
    setTransactions,
    setLoanTransactions,
    setLoans,
    setContacts,
    setSelectedAccount,
    setSelectedTransaction,
    setSelectedLoan,
    setLoading,
    setError,
    setCurrentPage,
    setHasMoreTransactions,
    initialFormData,
    setInitialFormData,

    // Actions
    login,
    logout,
    refreshData,
    fetchAccountTransactions,
    fetchLoanTransactionsForContact,
    goToLoans,
    goToLoanDetail,
    getAccountName,
    addTransaction,
    updateTransaction,
    deleteTransaction,
    settleLoan
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
